import * as Sentry from '@sentry/nextjs'
import { UserRejectedRequestError } from 'viem'

import type { Primitive } from '@sentry/core'

export const setSentryTag = (id: string, value: Primitive): void => {
  Sentry.setTag(id, value)
}

export const setSentryUserId = (id: string): void => {
  Sentry.setUser({ id })
}

export const addSentryBreadcrumb = (message: string) => {
  Sentry.addBreadcrumb({
    level: 'info',
    message,
  })
}

type JsonRpcError = {
  code?: number
  message?: string
  stack?: string
  data?: {
    code?: number
    message?: string
  }
}

const ERROR_SNIPPETS_TO_IGNORE = [
  'user denied transaction signature',
  'user rejected the transaction',
  'user rejected the request',
]

const shouldFilterError = (err: Error | JsonRpcError) => {
  if (
    ERROR_SNIPPETS_TO_IGNORE.some((snippet) =>
      err.message?.toLowerCase().includes(snippet),
    )
  ) {
    return true
  }

  return false
}

export const captureException = (
  err: unknown,
  context?: {
    tags?: Record<string, Primitive>
    extra?: Record<string, unknown>
  },
): void => {
  addSentryBreadcrumb('custom capture exception')

  if (process.env.APP_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.error(err)
  }

  // Don't need to capture these
  if (err instanceof UserRejectedRequestError) return

  if (err instanceof Error) {
    if (shouldFilterError(err)) return

    Sentry.captureException(err, context)
    return
  }

  addSentryBreadcrumb('rpc error')
  const rpcErr = err as JsonRpcError
  if (shouldFilterError(rpcErr)) return

  if (rpcErr?.data?.message) {
    Sentry.captureException(rpcErr.data.message, {
      ...context,
      extra: { ...context?.extra, rpcErr },
    })
    return
  }
  if (rpcErr?.message) {
    Sentry.captureException(rpcErr.message, {
      ...context,
      extra: { ...context?.extra, rpcErr },
    })
    return
  }
  Sentry.captureException(err, context)
}

export const captureMessage = (
  message: string,
  context?: {
    tags?: Record<string, Primitive>
    extra?: Record<string, unknown>
  },
): void => {
  Sentry.captureMessage(message, {
    ...context,
  })
  if (process.env.APP_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn(message, context?.extra)
  }
}

if (!process.env.SENTRY_DSN) {
  // eslint-disable-next-line no-console
  console.error('Sentry DSN not defined')
}
